'use client';

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { Table } from './table';

export interface DataTableColumn<T> {
  key: string;
  header: string;
  sortable?: boolean;
  locked?: boolean;
  render?: (value: unknown, row: T, index: number) => React.ReactNode;
  className?: string;
}

export interface BulkAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

export type DataTableMobileCardBreakpoint = 'md' | 'lg' | 'xl' | 'always';

const mobileCardBreakpointClasses: Record<DataTableMobileCardBreakpoint, string> = {
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
  always: '',
};

const tableBreakpointClasses: Record<DataTableMobileCardBreakpoint, string> = {
  md: 'hidden md:block',
  lg: 'hidden lg:block',
  xl: 'hidden xl:block',
  always: 'hidden',
};

export interface BulkActionBarProps {
  selectedCount: number;
  actions: BulkAction[];
  onClear: () => void;
}

export function BulkActionBar({ selectedCount, actions, onClear }: BulkActionBarProps) {
  if (selectedCount === 0) return null;
  return (
    <div className="experience-candidate-data-grid flex flex-wrap items-center gap-2 border-b border-statura-green-accessible/20 bg-statura-green-accessible/5 px-4 py-2.5">
      <span className="shrink-0 font-body text-body-sm font-semibold text-statura-green-accessible">
        {selectedCount} selected
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-body text-caption font-semibold transition-interactive duration-200',
              action.variant === 'destructive'
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-statura-green-accessible text-white hover:bg-statura-green-accessible-hover',
            )}
          >
            {action.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="inline-flex items-center gap-1 font-body text-caption text-text-muted transition-interactive duration-200 hover:text-text-body sm:ml-auto"
      >
        <X className="h-3 w-3" />
        Clear selection
      </button>
    </div>
  );
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  pageSize?: number;
  className?: string;
  emptyMessage?: string;
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: string | ((row: T, index: number) => string);
  /** Optional per-row `data-testid` (e.g. for E2E row targeting). */
  rowTestId?: string | ((row: T, index: number) => string);
  stickyHeader?: boolean;
  /** Optional: map of column key -> visible. Columns missing from the map default to visible. */
  columnVisibility?: Record<string, boolean>;
  /** Toolbar content rendered above the table (e.g., column visibility dropdown) */
  toolbar?: React.ReactNode;
  /** Enable row selection checkboxes */
  selectable?: boolean;
  /** Controlled set of selected row keys */
  selectedRows?: Set<string>;
  /** Called when selection changes */
  onSelectionChange?: (selected: Set<string>) => void;
  /** Function to extract a unique key from a row. Defaults to (row as any).id */
  rowKey?: (row: T) => string;
  /** Exact fixture/data revision for provider-runtime row evidence. */
  experienceEvidenceRevision?: string;
  /** Bulk actions shown when rows are selected */
  bulkActions?: BulkAction[];
  /** Optional compact row renderer used below the configured breakpoint instead of the full table. */
  mobileCardRenderer?: (row: T, index: number) => React.ReactNode;
  /** Breakpoint where the full table replaces mobile cards. Defaults to lg for dashboard sidebars. */
  mobileCardBreakpoint?: DataTableMobileCardBreakpoint;
  /**
   * Phase F1 Sprint 2 — controlled sort state. When supplied alongside
   * `onSortChange`, DataTable switches to controlled mode: it emits
   * sort changes via the callback and skips its internal client-side
   * sort (consumer is expected to re-fetch / re-order data server-side).
   * Backwards-compatible — absent props leave the existing internal
   * sort behaviour untouched.
   */
  sort?: SortState;
  onSortChange?: (next: SortState) => void;
  /**
   * Phase F1 Sprint 2 — page-size selector. When supplied, the footer
   * renders a `<select>` over these options + `onPageSizeChange`.
   * `pageSize` remains the active size; `onPageSizeChange` is fired
   * with the new value so the consumer can persist it (typically via
   * useLayoutPreferences).
   */
  pageSizeOptions?: ReadonlyArray<number>;
  onPageSizeChange?: (size: number) => void;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  key: string | null;
  direction: SortDirection;
}

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce((acc: unknown, part: string) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

function defaultRowKey<T>(row: T): string {
  return (row as Record<string, unknown>).id as string;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  pageSize = 10,
  className,
  emptyMessage = 'No data available',
  onRowClick,
  rowClassName,
  rowTestId,
  stickyHeader = false,
  columnVisibility,
  toolbar,
  selectable = false,
  selectedRows,
  onSelectionChange,
  rowKey = defaultRowKey,
  experienceEvidenceRevision,
  bulkActions = [],
  mobileCardRenderer,
  mobileCardBreakpoint = 'lg',
  sort: controlledSort,
  onSortChange,
  pageSizeOptions,
  onPageSizeChange,
}: DataTableProps<T>) {
  const [internalSort, setInternalSort] = useState<SortState>({ key: null, direction: null });
  // Phase F1 Sprint 2 — sort is controlled when BOTH `sort` prop and
  // `onSortChange` callback are provided. Otherwise we fall back to the
  // existing internal client-side sort.
  const isSortControlled = controlledSort !== undefined && onSortChange !== undefined;
  const sort: SortState = isSortControlled ? controlledSort! : internalSort;

  // Phase F1 S2 pass 1 P1 fix: `handleSort` previously closed over a
  // render-local `setSort` with `[]` deps, which captured stale `sort`
  // state and broke the asc → desc → clear cycle on repeated clicks.
  // Compute the next state from CURRENT `sort` inline + read controlled
  // mode from a ref to keep the callback dep-stable.
  const sortRef = useRef(sort);
  sortRef.current = sort;
  const isSortControlledRef = useRef(isSortControlled);
  isSortControlledRef.current = isSortControlled;
  const onSortChangeRef = useRef(onSortChange);
  onSortChangeRef.current = onSortChange;

  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = useCallback((key: string) => {
    const prev = sortRef.current;
    const next: SortState =
      prev.key !== key
        ? { key, direction: 'asc' }
        : prev.direction === 'asc'
          ? { key, direction: 'desc' }
          : prev.direction === 'desc'
            ? { key: null, direction: null }
            : { key, direction: 'asc' };
    if (isSortControlledRef.current && onSortChangeRef.current) {
      onSortChangeRef.current(next);
    } else {
      setInternalSort(next);
    }
    setCurrentPage(1);
  }, []);

  // Filter columns by visibility (if provided)
  const visibleColumns = useMemo(() => {
    if (!columnVisibility) return columns;
    return columns.filter((col) => col.locked || columnVisibility[col.key] !== false);
  }, [columns, columnVisibility]);

  const sortedData = useMemo(() => {
    // Phase F1 Sprint 2 — when sort is controlled, trust the caller's data
    // (server already sorted it). The headers still show the sort indicator
    // via `sort` state, but DataTable skips its local sort pass.
    if (isSortControlled) return data;
    if (!sort.key || !sort.direction) return data;
    return [...data].sort((a, b) => {
      const aVal = getNestedValue(a, sort.key!);
      const bVal = getNestedValue(b, sort.key!);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        // BUG-058: Detect ISO date strings and sort numerically instead of alphabetically
        const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
        if (ISO_DATE_RE.test(aVal) && ISO_DATE_RE.test(bVal)) {
          const diff = new Date(aVal).getTime() - new Date(bVal).getTime();
          return sort.direction === 'asc' ? diff : -diff;
        }
        const cmp = aVal.localeCompare(bVal);
        return sort.direction === 'asc' ? cmp : -cmp;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sort.direction === 'asc' ? cmp : -cmp;
    });
  }, [data, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Selection helpers
  const selection = selectedRows ?? new Set<string>();
  const paginatedKeys = useMemo(
    () => paginatedData.map((row) => rowKey(row)),
    [paginatedData, rowKey],
  );
  const allPageSelected = paginatedKeys.length > 0 && paginatedKeys.every((k) => selection.has(k));
  const somePageSelected = !allPageSelected && paginatedKeys.some((k) => selection.has(k));

  const handleToggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (allPageSelected) {
      paginatedKeys.forEach((k) => next.delete(k));
    } else {
      paginatedKeys.forEach((k) => next.add(k));
    }
    onSelectionChange(next);
  }, [onSelectionChange, selection, allPageSelected, paginatedKeys]);

  const handleToggleRow = useCallback(
    (key: string) => {
      if (!onSelectionChange) return;
      const next = new Set(selection);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onSelectionChange(next);
    },
    [onSelectionChange, selection],
  );

  const handleClearSelection = useCallback(() => {
    if (onSelectionChange) onSelectionChange(new Set());
  }, [onSelectionChange]);

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sort.key !== columnKey) {
      return (
        <ChevronsUpDown aria-hidden="true" className="ml-1 inline h-3.5 w-3.5 text-text-subtle" />
      );
    }
    return sort.direction === 'asc' ? (
      <ChevronUp
        aria-hidden="true"
        className="ml-1 inline h-3.5 w-3.5 text-statura-green-accessible"
      />
    ) : (
      <ChevronDown
        aria-hidden="true"
        className="ml-1 inline h-3.5 w-3.5 text-statura-green-accessible"
      />
    );
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent, callback: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  }, []);

  const totalColSpan = visibleColumns.length + (selectable ? 1 : 0);

  return (
    <div
      className={cn(
        'experience-candidate-data-grid w-full min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm',
        className,
      )}
    >
      {/* Toolbar area (e.g., column visibility dropdown) */}
      {toolbar && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-b border-slate-100 bg-white px-4 py-2">
          {toolbar}
        </div>
      )}

      {/* Bulk action bar */}
      {selectable && (
        <BulkActionBar
          selectedCount={selection.size}
          actions={bulkActions}
          onClear={handleClearSelection}
        />
      )}

      {mobileCardRenderer && (
        <div className={mobileCardBreakpointClasses[mobileCardBreakpoint]} data-mobile-cards="true">
          {paginatedData.length === 0 ? (
            <div className="px-4 py-12 text-center font-body text-body-sm text-text-muted">
              {emptyMessage}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {paginatedData.map((row, rowIndex) => {
                const globalIndex = (currentPage - 1) * pageSize + rowIndex;
                const key = rowKey(row);
                const isSelected = selection.has(key);
                const rowCls =
                  typeof rowClassName === 'function'
                    ? rowClassName(row, globalIndex)
                    : rowClassName;
                const testId =
                  typeof rowTestId === 'function' ? rowTestId(row, globalIndex) : rowTestId;
                const experienceEvidence = experienceRowEvidence(row, key);

                return (
                  <div
                    key={key || rowIndex}
                    data-testid={testId ? `${testId}-mobile` : undefined}
                    data-experience-entity-ids={
                      experienceEvidenceRevision
                        ? experienceEvidence.entityIds.join('|') || undefined
                        : undefined
                    }
                    data-experience-outcomes={
                      experienceEvidenceRevision
                        ? experienceEvidence.outcomeTokens.join('|') || undefined
                        : undefined
                    }
                    data-experience-evidence-revision={experienceEvidenceRevision}
                    className={cn(
                      'px-4 py-3 transition-interactive duration-200',
                      onRowClick &&
                        'cursor-pointer hover:bg-surface-soft/70 focus-visible:bg-surface-soft/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-statura-green-accessible',
                      selectable && isSelected && 'bg-statura-green-accessible/[0.04]',
                      rowCls,
                    )}
                    onClick={onRowClick ? () => onRowClick(row, globalIndex) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => handleKeyDown(e, () => onRowClick(row, globalIndex))
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'button' : undefined}
                  >
                    <div className="flex items-start gap-3">
                      {selectable && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRow(key)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 h-4 w-4 cursor-pointer rounded border-slate-300 text-statura-green-accessible accent-statura-green-accessible focus:ring-statura-green-accessible/30"
                          aria-label={`Select row ${globalIndex + 1}`}
                        />
                      )}
                      <div className="min-w-0 flex-1">{mobileCardRenderer(row, globalIndex)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div
        className={cn(
          'max-w-full overflow-x-auto',
          mobileCardRenderer && tableBreakpointClasses[mobileCardBreakpoint],
        )}
      >
        <Table
          role="table"
          data-v2-table="true"
          className="statura-data-table w-full min-w-full border-collapse text-sm"
        >
          <thead>
            <tr className={cn('border-b border-slate-200 bg-surface-soft')}>
              {selectable && (
                <th
                  className={cn(
                    'w-10 bg-surface-soft px-3 py-2.5 text-center',
                    stickyHeader && 'sticky top-0 z-10',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected;
                    }}
                    onChange={handleToggleAll}
                    className="h-4 w-4 cursor-pointer rounded border-slate-300 text-statura-green-accessible accent-statura-green-accessible focus:ring-statura-green-accessible/30"
                    aria-label="Select all rows on this page"
                  />
                </th>
              )}
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'bg-surface-soft px-3 py-2.5 text-left align-top font-body text-eyebrow uppercase text-text-muted sm:px-4',
                    stickyHeader && 'sticky top-0 z-10',
                    col.sortable &&
                      'cursor-pointer select-none transition-interactive duration-200 hover:text-text-heading',
                    col.className,
                  )}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  onKeyDown={
                    col.sortable ? (e) => handleKeyDown(e, () => handleSort(col.key)) : undefined
                  }
                  tabIndex={col.sortable ? 0 : undefined}
                  role={col.sortable ? 'button' : undefined}
                  aria-sort={
                    sort.key === col.key
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <span className="inline-flex min-w-0 items-center break-words">
                    {col.header}
                    {col.sortable && <SortIcon columnKey={col.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={totalColSpan}
                  className="px-4 py-12 text-center font-body text-body-sm text-text-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => {
                const globalIndex = (currentPage - 1) * pageSize + rowIndex;
                const key = rowKey(row);
                const isSelected = selection.has(key);
                const rowCls =
                  typeof rowClassName === 'function'
                    ? rowClassName(row, globalIndex)
                    : rowClassName;
                const testId =
                  typeof rowTestId === 'function' ? rowTestId(row, globalIndex) : rowTestId;
                const experienceEvidence = experienceRowEvidence(row, key);
                return (
                  <tr
                    key={key ?? rowIndex}
                    data-testid={testId}
                    data-experience-entity-ids={
                      experienceEvidenceRevision
                        ? experienceEvidence.entityIds.join('|') || undefined
                        : undefined
                    }
                    data-experience-outcomes={
                      experienceEvidenceRevision
                        ? experienceEvidence.outcomeTokens.join('|') || undefined
                        : undefined
                    }
                    data-experience-evidence-revision={experienceEvidenceRevision}
                    className={cn(
                      'border-b border-slate-100 transition-interactive duration-200 last:border-0',
                      onRowClick &&
                        'cursor-pointer hover:bg-surface-soft/70 focus-visible:bg-surface-soft/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-statura-green-accessible',
                      selectable && isSelected && 'bg-statura-green-accessible/[0.04]',
                      rowCls,
                    )}
                    onClick={onRowClick ? () => onRowClick(row, globalIndex) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => handleKeyDown(e, () => onRowClick(row, globalIndex))
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : undefined}
                    role={onRowClick ? 'button' : undefined}
                  >
                    {selectable && (
                      <td className="w-10 px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRow(key)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 cursor-pointer rounded border-slate-300 text-statura-green-accessible accent-statura-green-accessible focus:ring-statura-green-accessible/30"
                          aria-label={`Select row ${globalIndex + 1}`}
                        />
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'min-w-0 break-words px-3 py-2.5 align-top font-body text-body-sm text-text-heading [overflow-wrap:anywhere] sm:px-4',
                          col.className,
                        )}
                      >
                        {col.render
                          ? col.render(getNestedValue(row, col.key), row, globalIndex)
                          : ((getNestedValue(row, col.key) as React.ReactNode) ?? '\u2014')}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {(totalPages > 1 || (pageSizeOptions && pageSizeOptions.length > 1)) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
          <span className="font-body text-caption text-text-muted">
            {totalPages > 1 ? (
              <>
                Showing {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length}
              </>
            ) : (
              <>{sortedData.length} rows</>
            )}
            {pageSizeOptions && pageSizeOptions.length > 1 && onPageSizeChange && (
              <>
                {' '}
                ·{' '}
                <label className="inline-flex items-center gap-1">
                  <span>Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const next = Number.parseInt(e.target.value, 10);
                      if (!Number.isFinite(next)) return;
                      onPageSizeChange(next);
                      setCurrentPage(1);
                    }}
                    aria-label="Rows per page"
                    className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[11.5px] text-statura-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible/40"
                  >
                    {pageSizeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
          </span>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-text-body transition-interactive duration-200 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                if (totalPages <= 7) return true;
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - currentPage) <= 1) return true;
                return false;
              })
              .map((page, idx, arr) => {
                const showEllipsis = idx > 0 && page - (arr[idx - 1] ?? 0) > 1;
                return (
                  <React.Fragment key={page}>
                    {showEllipsis && (
                      <span className="px-1 font-body text-caption text-text-subtle">...</span>
                    )}
                    <button
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-lg font-body text-caption font-semibold transition-interactive duration-200',
                        page === currentPage
                          ? 'bg-statura-ink-dark text-white'
                          : 'text-text-body hover:bg-slate-100',
                      )}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-text-body transition-interactive duration-200 hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function experienceRowEvidence(row: Record<string, unknown>, key: string) {
  const entityIds = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value === 'string' && value.length > 0) entityIds.add(value);
  };
  add(key);
  for (const field of [
    'id',
    'client_id',
    'resident_id',
    'staff_id',
    'care_plan_id',
    'program_enrollment_id',
  ]) {
    add(row[field]);
  }
  const metadata =
    row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
      ? (row.metadata as Record<string, unknown>)
      : {};
  add(metadata.supersedesEnrollmentId);
  add(metadata.supersededByEnrollmentId);

  const primaryId = typeof row.id === 'string' ? row.id : key;
  const outcomeTokens: string[] = [];
  const addOutcome = (field: string, value: unknown, present: boolean) => {
    if (!present) return;
    const normalized = value === null ? 'null' : String(value);
    outcomeTokens.push(`entity:${primaryId}.${field}=${normalized}`);
  };
  for (const field of [
    'status',
    'means_testing_status',
    'means_tested_fee',
    'annual_income',
    'authority',
    'program_transition',
    'integration',
  ]) {
    addOutcome(field, row[field], Object.prototype.hasOwnProperty.call(row, field));
  }
  const customFields =
    row.custom_fields && typeof row.custom_fields === 'object' && !Array.isArray(row.custom_fields)
      ? (row.custom_fields as Record<string, unknown>)
      : {};
  addOutcome('open_matter', customFields.openMatter, 'openMatter' in customFields);
  return { entityIds: [...entityIds], outcomeTokens };
}
